// Settings — sidebar tabs for Defaults, Company context, Brains, Account & usage,
// Team & admin, Power Mode. All tabs are interactive.

import { useState } from 'react';
import * as api from '../api';
import type { Settings as SettingsT, CompanyContext, Brain } from '../types';
import { ElementCell } from '../components/ElementCell';

type Tab = 'defaults' | 'company' | 'brains' | 'account' | 'team' | 'power';

interface Props {
  settings: SettingsT;
  companyContext: CompanyContext | null;
  allBrains: Brain[];
  onSettingsChange: (s: SettingsT) => void;
  onCompanyContextChange: (c: CompanyContext | null) => void;
}

const TABS: { id: Tab; label: string }[] = [
  { id: 'defaults', label: 'Defaults' },
  { id: 'company', label: 'Company context' },
  { id: 'brains', label: 'Brains' },
  { id: 'account', label: 'Account & usage' },
  { id: 'team', label: 'Team & admin' },
  { id: 'power', label: 'Power Mode' },
];

export function Settings({ settings, companyContext, allBrains, onSettingsChange, onCompanyContextChange }: Props) {
  const [tab, setTab] = useState<Tab>('defaults');
  const [pending, setPending] = useState(false);

  async function patchSettings(p: Partial<SettingsT>) {
    setPending(true);
    try {
      const next = await api.updateSettings(p);
      onSettingsChange(next);
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 md:px-8 py-8 md:py-12 pb-20 grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6 md:gap-12" style={{ opacity: pending ? 0.7 : 1 }}>
      <aside className="md:sticky md:top-20 md:self-start">
        <div className="text-[11px] text-text3 mb-3">Settings</div>
        <nav className="flex md:flex-col gap-1 md:gap-0.5 overflow-x-auto md:overflow-visible -mx-1 px-1 pb-2 md:pb-0">
          {TABS.map((t) => (
            <SidebarLink key={t.id} active={tab === t.id} onClick={() => setTab(t.id)}>{t.label}</SidebarLink>
          ))}
        </nav>
      </aside>

      <main className="min-w-0">
        {tab === 'defaults' && <DefaultsSection settings={settings} onPatch={patchSettings} />}
        {tab === 'company' && (
          <CompanySection
            companyContext={companyContext}
            onChange={onCompanyContextChange}
          />
        )}
        {tab === 'brains' && <BrainsSection brains={allBrains} />}
        {tab === 'account' && <AccountSection settings={settings} companyContext={companyContext} />}
        {tab === 'team' && <PlaceholderSection title="Team & admin" body="Multi-user support arrives with v2. For now, this account is single-tenant. The team & admin surface will manage seats, role-based brain access, and per-user audit." />}
        {tab === 'power' && <PowerModeSection settings={settings} onPatch={patchSettings} />}
      </main>
    </div>
  );
}

// ─── Defaults tab ────────────────────────────────────────────────
function DefaultsSection({ settings, onPatch }: { settings: SettingsT; onPatch: (p: Partial<SettingsT>) => void }) {
  return (
    <>
      <div className="text-[11px] text-text3 mb-2">Settings · Defaults</div>
      <h1 className="font-display text-[24px] sm:text-[28px] md:text-[32px] leading-[1.15] text-ink tracking-tight2 mb-1.5">How Element IQ behaves.</h1>
      <p className="text-sm text-text2 mb-10 max-w-[600px]">
        By default, Element IQ auto-selects brains and models for you. You can override anything per-message, or turn auto off entirely with Power Mode.
      </p>

      <Group title="Brain selection" desc="When you start a chat, Element IQ picks the brains that match your prompt + company context. The brain set locks for the rest of that thread.">
        <Row label="Auto-select brains for new chats" help={<>Off → you'll pick brains manually before each new chat. <strong className="text-ink font-semibold">On is recommended.</strong></>}>
          <Toggle on={settings.auto_brain} onChange={(v) => onPatch({ auto_brain: v })} />
        </Row>
        <Row label="Maximum brains per chat" help={<>Higher counts give richer context but cost more tokens. <strong className="text-ink font-semibold">5 is recommended.</strong></>}>
          <Select value={String(settings.max_brains_per_chat)} options={['3','4','5','6','7','8']} onChange={(v) => onPatch({ max_brains_per_chat: parseInt(v, 10) })} suffix=" brains max" />
        </Row>
      </Group>

      <Group title="Model selection" desc="Element IQ picks the right model for each message automatically. The selection happens per-message, invisibly — you don't manage it.">
        <Row label="Auto-select model per message" help={<>Off → you pick a model before every message. <strong className="text-ink font-semibold">On is recommended.</strong></>}>
          <Toggle on={settings.auto_model} onChange={(v) => onPatch({ auto_model: v })} />
        </Row>
      </Group>

      <Group title="Default lens" desc="The lens toggle decides whether each answer is purely from brain knowledge (Objective) or filtered through your company context (Through Element's lens).">
        <Row label="When in doubt, use this lens">
          <Select
            value={settings.default_lens === 'objective' ? 'Objective' : "Element's lens"}
            options={['Objective', "Element's lens"]}
            onChange={(v) => onPatch({ default_lens: v === 'Objective' ? 'objective' : 'lens' })}
          />
        </Row>
      </Group>
    </>
  );
}

// ─── Company context tab ─────────────────────────────────────────
function CompanySection({
  companyContext, onChange,
}: { companyContext: CompanyContext | null; onChange: (c: CompanyContext | null) => void }) {
  const [draft, setDraft] = useState({
    org_name: companyContext?.org_name ?? '',
    industry: companyContext?.industry ?? '',
    role: companyContext?.role ?? '',
    focus_areas: companyContext?.focus_areas ?? '',
    key_terms: companyContext?.key_terms ?? '',
    free_text: companyContext?.free_text ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  async function save() {
    setSaving(true);
    try {
      const next = await api.updateCompanyContext(draft);
      onChange(next);
      setSavedAt(Date.now());
    } finally {
      setSaving(false);
    }
  }

  const showSaved = savedAt !== null && Date.now() - savedAt < 4000;

  return (
    <>
      <div className="text-[11px] text-text3 mb-2">Settings · Company context</div>
      <h1 className="font-display text-[24px] sm:text-[28px] md:text-[32px] leading-[1.15] text-ink tracking-tight2 mb-1.5">What Element IQ knows about you.</h1>
      <p className="text-sm text-text2 mb-10 max-w-[640px]">
        This block fuels the auto-brain matcher and powers "Through Element's lens" answers. The richer this is, the better the lens-mode answers.
      </p>

      <Group title="The basics" desc="Used by the brain matcher and surfaced in lens-mode answers.">
        <FieldRow label="Organization name">
          <TextInput value={draft.org_name} onChange={(v) => setDraft({ ...draft, org_name: v })} />
        </FieldRow>
        <FieldRow label="Industry">
          <TextInput value={draft.industry} onChange={(v) => setDraft({ ...draft, industry: v })} placeholder="e.g. Critical minerals processing" />
        </FieldRow>
        <FieldRow label="Your role">
          <TextInput value={draft.role} onChange={(v) => setDraft({ ...draft, role: v })} placeholder="e.g. Process engineer · CTO · Project manager" />
        </FieldRow>
      </Group>

      <Group title="Focus + vocabulary" desc="What you're working on and the terms you use. Comma-separated lists work best.">
        <FieldRow label="Focus areas">
          <TextInput value={draft.focus_areas} onChange={(v) => setDraft({ ...draft, focus_areas: v })} placeholder="e.g. scandium recovery, bauxite residue, Higgins I" />
        </FieldRow>
        <FieldRow label="Key terms">
          <TextInput value={draft.key_terms} onChange={(v) => setDraft({ ...draft, key_terms: v })} placeholder="e.g. REE, ICP-MS, Maaden, syngas" />
        </FieldRow>
      </Group>

      <Group title="Free-form context" desc="Anything else worth knowing. Project background, current priorities, what good looks like for you.">
        <textarea
          value={draft.free_text}
          onChange={(e) => setDraft({ ...draft, free_text: e.target.value })}
          rows={6}
          placeholder="e.g. We're a critical-minerals processor in Louisiana focused on scandium and rare-earth recovery from refinery byproducts. Currently piloting at Higgins I and evaluating commercial-scale flowsheet for Q3."
          className="w-full bg-surface border border-border rounded-md p-3 text-[14px] leading-relaxed text-ink placeholder:text-text3 outline-none focus:border-ink/40 resize-y"
        />
      </Group>

      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="bg-ink text-bg rounded-md px-4 py-2 text-sm font-medium hover:bg-accent transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save context'}
        </button>
        {showSaved && <span className="text-[12px] text-text2">Saved.</span>}
      </div>
    </>
  );
}

// ─── Brains tab ───────────────────────────────────────────────────
function BrainsSection({ brains }: { brains: Brain[] }) {
  const [query, setQuery] = useState('');
  const filtered = brains.filter((b) => {
    const q = query.toLowerCase();
    if (!q) return true;
    return (b.name + ' ' + b.description + ' ' + b.tags + ' ' + b.domain).toLowerCase().includes(q);
  });
  return (
    <>
      <div className="text-[11px] text-text3 mb-2">Settings · Brains</div>
      <h1 className="font-display text-[24px] sm:text-[28px] md:text-[32px] leading-[1.15] text-ink tracking-tight2 mb-1.5">The knowledge layer.</h1>
      <p className="text-sm text-text2 mb-8 max-w-[640px]">
        Brains are domain-expertise modules — what makes Element IQ better than a generic LLM. The auto-matcher picks from this library; you can also lock specific brains per chat.
      </p>

      <div className="mb-8">
        <div className="text-[11px] text-text3 mb-3">Connected data sources</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
          <IntegrationCard
            name="USGS Mineral Resources Program"
            sub="Producer + reserve characterization across critical minerals."
            status="connected"
          />
          <IntegrationCard
            name="Prospector by Emily King"
            sub="Critical-mineral repository — sourcing, offtakes, supply intelligence."
            status="connected"
          />
          <IntegrationCard
            name="DOE Critical Materials Assessments"
            sub="Secondary-source recovery R&D, OAM / NETL / PNNL programs."
            status="connected"
          />
          <IntegrationCard
            name="ElementUSA proprietary brains"
            sub="Authored by ElementUSA + SRNL experts; locked to your org."
            status="connected"
          />
        </div>
        <div className="text-[11px] text-text3 mt-2.5">
          Brain authors: USGS · DOE · ElementUSA · SRNL · select licensed third parties. New sources land monthly.
        </div>
      </div>

      <div className="text-[11px] text-text3 mb-2.5">Browse brains</div>
      <div className="mb-5">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search brains by name, tag, or domain…"
          className="w-full bg-surface border border-border rounded-md px-4 py-2.5 text-[14px] text-ink placeholder:text-text3 outline-none focus:border-ink/40"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {filtered.map((b) => (
          <div key={b.id} className="bg-surface border border-border rounded-lg p-4 flex gap-3.5 items-start">
            <ElementCell symbol={b.symbol} atomicNumber={b.atomic_number} size="sm" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <div className="text-[14px] font-medium text-ink leading-snug truncate">{b.name}</div>
                {b.domain === 'supply' && (
                  <span className="text-[9px] font-mono text-accent bg-accent/[0.08] border border-accent/30 rounded-full px-1.5 py-0 leading-tight flex-shrink-0">
                    SUPPLY
                  </span>
                )}
              </div>
              <div className="text-[12px] text-text2 mt-1 leading-snug line-clamp-2">{b.description}</div>
              <div className="font-mono text-[10px] text-text3 mt-2 truncate">{b.org} · v{b.version} · {b.author}</div>
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-text3 text-sm py-8 text-center">No brains match your search.</div>
      )}
    </>
  );
}

function IntegrationCard({ name, sub, status }: { name: string; sub: string; status: 'connected' | 'pending' }) {
  return (
    <div className="bg-surface border border-border rounded-md px-3.5 py-2.5 flex items-start gap-2.5">
      <span className={`mt-1 inline-flex items-center justify-center w-2 h-2 rounded-full flex-shrink-0 ${status === 'connected' ? 'bg-accent' : 'bg-text3'}`} />
      <div className="min-w-0 flex-1">
        <div className="text-[13px] text-ink font-medium leading-snug truncate">{name}</div>
        <div className="text-[11px] text-text2 leading-snug mt-0.5">{sub}</div>
      </div>
      <span className={`text-[9px] font-mono uppercase tracking-wide flex-shrink-0 mt-1 ${status === 'connected' ? 'text-text2' : 'text-text3'}`}>
        {status}
      </span>
    </div>
  );
}

// ─── Account & usage tab ─────────────────────────────────────────
function AccountSection({ settings, companyContext }: { settings: SettingsT; companyContext: CompanyContext | null }) {
  const usedPct = settings.daily_limit_cents > 0
    ? Math.min(100, (settings.daily_used_cents / settings.daily_limit_cents) * 100)
    : 0;

  return (
    <>
      <div className="text-[11px] text-text3 mb-2">Settings · Account & usage</div>
      <h1 className="font-display text-[24px] sm:text-[28px] md:text-[32px] leading-[1.15] text-ink tracking-tight2 mb-1.5">Your usage at a glance.</h1>
      <p className="text-sm text-text2 mb-10 max-w-[600px]">
        Element IQ runs on a daily compute budget. Most users never approach it. The numbers below are for transparency — they don't gate your work unless you actually exceed the limit.
      </p>

      <Group title="Today's usage" desc="Resets at midnight UTC.">
        <div className="py-4">
          <div className="bg-bgsoft rounded-full h-2 overflow-hidden mb-2">
            <div className="h-full bg-ink rounded-full transition-all" style={{ width: `${usedPct}%` }} />
          </div>
          <div className="flex justify-between font-mono text-xs text-text2">
            <span>${(settings.daily_used_cents / 100).toFixed(2)} used today</span>
            <span>${(settings.daily_limit_cents / 100).toFixed(2)} daily limit</span>
          </div>
        </div>
      </Group>

      <Group title="Account" desc="Single-tenant for v1. Multi-user in v2.">
        <Row label="Account holder">
          <span className="font-mono text-xs text-text2">demo@element-iq.local</span>
        </Row>
        {companyContext?.org_name && (
          <Row label="Organization" help="Edit in the Company context tab.">
            <span className="font-mono text-xs text-text2">{companyContext.org_name}</span>
          </Row>
        )}
      </Group>
    </>
  );
}

// ─── Power Mode tab ──────────────────────────────────────────────
function PowerModeSection({ settings, onPatch }: { settings: SettingsT; onPatch: (p: Partial<SettingsT>) => void }) {
  return (
    <>
      <div className="text-[11px] text-text3 mb-2">Settings · Power Mode</div>
      <h1 className="font-display text-[24px] sm:text-[28px] md:text-[32px] leading-[1.15] text-ink tracking-tight2 mb-1.5">For power users and audit trails.</h1>
      <p className="text-sm text-text2 mb-10 max-w-[640px]">
        Power Mode reveals the moving parts. You'll see the model used per answer, token counts, durations, and the cost per message. Most users should leave this off.
      </p>

      <div className="bg-ink text-bg rounded-lg p-6 mb-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-48 h-48 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(255,43,10,0.10), transparent 70%)' }} />
        <div className="relative inline-flex items-center gap-1.5 bg-accent/10 text-accent px-2.5 py-1 rounded-full font-mono text-[11px] font-medium mb-3">
          <span className="w-1.5 h-1.5 bg-accent rounded-full" />
          Power Mode
        </div>
        <div className="font-display text-[15px] mb-1.5 text-bg">Show model details, token counts, and per-message cost.</div>
        <DarkRow label="Reveal Power Mode UI" help={<>When on, every assistant answer surfaces its model, tokens, and cost. Daily-usage chip also appears in the header. <span className="text-accent font-semibold">For audit-driven workflows and technical evaluators.</span></>}>
          <DarkToggle on={settings.advanced_mode} onChange={(v) => onPatch({ advanced_mode: v })} />
        </DarkRow>
      </div>

      <Group title="Manual selection (advanced)" desc="Turn off auto-select for brains and models if you want to drive everything manually for every message.">
        <Row label="Always require manual brain selection">
          <Toggle on={!settings.auto_brain} onChange={(v) => onPatch({ auto_brain: !v })} />
        </Row>
        <Row label="Always require manual model selection">
          <Toggle on={!settings.auto_model} onChange={(v) => onPatch({ auto_model: !v })} />
        </Row>
      </Group>
    </>
  );
}

// ─── Generic placeholder ─────────────────────────────────────────
function PlaceholderSection({ title, body }: { title: string; body: string }) {
  return (
    <>
      <div className="text-[11px] text-text3 mb-2">Settings · {title}</div>
      <h1 className="font-display text-[28px] leading-[1.15] text-ink tracking-tight2 mb-3">Coming with v2.</h1>
      <p className="text-sm text-text2 max-w-[640px] leading-relaxed">{body}</p>
    </>
  );
}

// ─── Building blocks ─────────────────────────────────────────────
function SidebarLink({ children, active = false, onClick }: { children: React.ReactNode; active?: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`text-sm font-medium px-3 py-2 rounded transition-all block text-left ${
        active ? 'bg-ink text-bg' : 'text-text2 hover:bg-bgsoft hover:text-ink'
      }`}
    >
      {children}
    </button>
  );
}

function Group({ title, desc, children }: { title: string; desc: string; children: React.ReactNode }) {
  return (
    <div className="bg-surface border border-border rounded-lg mb-6 overflow-hidden">
      <div className="px-6 pt-6 pb-4 border-b border-hairline">
        <div className="font-display text-[15px] text-ink mb-1">{title}</div>
        <div className="text-xs text-text2 leading-snug">{desc}</div>
      </div>
      <div className="px-6 py-2 pb-6">{children}</div>
    </div>
  );
}

function Row({ label, help, children }: { label: string; help?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex justify-between items-start py-4 border-b border-hairline last:border-b-0 gap-8">
      <div className="flex-1">
        <div className="text-sm text-ink font-medium mb-1">{label}</div>
        {help && <div className="text-xs text-text2 leading-snug max-w-[460px]">{help}</div>}
      </div>
      <div className="flex-shrink-0">{children}</div>
    </div>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="py-3 border-b border-hairline last:border-b-0">
      <label className="block text-[12px] text-text2 mb-1.5 font-medium">{label}</label>
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-bg/40 border border-border rounded-md px-3 py-2 text-[14px] text-ink placeholder:text-text3 outline-none focus:border-ink/40 focus:bg-surface transition-colors"
    />
  );
}

function DarkRow({ label, help, children }: { label: string; help?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex justify-between items-start py-4 border-b border-[#444] last:border-b-0 gap-8 relative">
      <div className="flex-1">
        <div className="text-sm text-bg font-medium mb-1">{label}</div>
        {help && <div className="text-xs text-[#b8b8b8] leading-snug max-w-[460px]">{help}</div>}
      </div>
      <div className="flex-shrink-0">{children}</div>
    </div>
  );
}

function Toggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!on)}
      className={`w-[42px] h-6 rounded-full relative transition-colors ${on ? 'bg-ink' : 'bg-border'}`}
      aria-pressed={on}
    >
      <span
        className="absolute top-0.5 left-0.5 w-5 h-5 bg-surface rounded-full shadow transition-transform"
        style={{ transform: on ? 'translateX(18px)' : 'translateX(0)' }}
      />
    </button>
  );
}

function DarkToggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!on)}
      className={`w-[42px] h-6 rounded-full relative transition-colors ${on ? 'bg-accent' : 'bg-[#444]'}`}
      aria-pressed={on}
    >
      <span
        className="absolute top-0.5 left-0.5 w-5 h-5 bg-bg rounded-full shadow transition-transform"
        style={{ transform: on ? 'translateX(18px)' : 'translateX(0)' }}
      />
    </button>
  );
}

function Select({ value, options, onChange, suffix = '' }: { value: string; options: string[]; onChange: (v: string) => void; suffix?: string }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-surface border border-border rounded-md px-3 py-1.5 font-sans text-[13px] text-ink cursor-pointer min-w-[180px] outline-none focus:border-ink"
    >
      {options.map((o) => (
        <option key={o} value={o}>{o}{suffix}</option>
      ))}
    </select>
  );
}
